import { useState, useEffect, useMemo, useCallback, forwardRef, useRef } from 'react';
import MuiAutocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Popper from '@mui/material/Popper';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import throttle from 'lodash/throttle';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { styled } from '@mui/material/styles';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { trackAutocompleteSearchAction } from '@Components/Utilities/Tracking';
import translator from '@Components/Utilities/Translator';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { setSearchHistoryCookie, getSearchHistoryValues } from '@Components/Utilities/CookieHelper';
import ListSubheader from '@mui/material/ListSubheader';
import Collapse from '@mui/material/Collapse';

const ListeningIndicator = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    marginRight: '10px',
    '& .listening-text': {
        fontWeight: '500'
    },
    '& .soundwave': {
        display: 'flex',
        gap: '2px',
        alignItems: 'center',
        '& .wave': {
            width: '3px',
            borderRadius: '20px',
            animation: 'soundwave 1.2s ease-in-out infinite',
            '&:nth-of-type(1)': {
                height: '15px',
                animationDelay: '0s',
                backgroundColor: '#999' // Light gray
            },
            '&:nth-of-type(2)': {
                height: '19px',
                animationDelay: '0.1s',
                backgroundColor: '#666' // Medium gray
            },
            '&:nth-of-type(3)': {
                height: '13px',
                animationDelay: '0.2s',
                backgroundColor: '#444' // Dark gray
            },
            '&:nth-of-type(4)': {
                height: '17px',
                animationDelay: '0.3s',
                backgroundColor: '#999' // Light gray (repeating pattern)
            },
            '&:nth-of-type(5)': {
                height: '15px',
                animationDelay: '0.4s',
                backgroundColor: '#666' // Medium gray (repeating pattern)
            }
        }
    },
    '@keyframes soundwave': {
        '0%, 40%, 100%': {
            transform: 'scaleY(0.4)'
        },
        '20%': {
            transform: 'scaleY(1.0)'
        }
    }
});

const Autocomplete = forwardRef((props, ref) => {
    const [history, setHistory] = useState();
    const [optionsVisible, setOptionsVisible] = useState(true);
    const collapseTimeoutRef = useRef(null);
    const popperRef = useRef(null);

    const SearchIconButton = styled(IconButton)(({ theme }) => ({
        color: theme.palette.common.white,
        backgroundColor: theme.palette.common.black,
        boxShadow: '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)',
        transition: 'all .2s ease-in-out',
        height: '32px',
        width: '32px',
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        },
        '&:focus-visible': {
            outline: '3px solid ' + theme.palette.primary.main,
            outlineOffset: '2px',
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText
        },
    }));

    const quickFindRegex = /^([a-z]{2})(\d{2})([a-z]{2})$/i;
    const defaultFilterOptions = createFilterOptions();
    const routes = useRecoilValue(atoms.routes);
    const onFilterOptions = (options, state) => {
        if (options?.some(op => op.isParent)) {
            let filteredOps = defaultFilterOptions(options, state);
            let valsToKeep = [];
            for (var i = 0; i < filteredOps.length; i++) {
                if (filteredOps[i].parentValue) {
                    valsToKeep.push(filteredOps[i].parentValue);
                }
                valsToKeep.push(filteredOps[i].value);
            }
            return options.filter(op => valsToKeep.includes(op.value));
        }
        else {
            return defaultFilterOptions(options, state);
        }
    };

    const ajaxOptions = {
        enabled: true,
        url: routes.ListingsAutocomplete,
        method: 'get',
        additionalData: null,
        dataKey: 'keywords',
        minLength: 3,
        onSearch: (active, searchValue, searchType) => {
            if (props.isFocused === false) return;

            if (searchType != 'Part') {
                setIsLoading(true);

                let url = ajaxOptions.url;
                if (ajaxOptions.method == 'get') {
                    url = buildUrl(url, ajaxOptions.dataKey, searchValue);

                    if (searchType == 'Attachment') {
                        url += `&SearchType=Attachment`;
                    } else if (searchType == 'Dismantled') {
                        url += `&Condition=Dismantled`;
                    }

                    if (ajaxOptions.additionalData) {
                        for (var key in ajaxOptions.additionalData) {
                            url += `&${key}=${ajaxOptions.additionalData[key]}`;
                        }
                    }
                }

                sandhillsRequest({
                    url: url,
                    method: ajaxOptions.method,
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((data) => {
                    if (active) {
                        let newOptions = [];

                        if (data) {
                            newOptions = [...newOptions, ...data];
                        }

                        setOptions(newOptions);
                        setIsLoading(false);
                    }
                });
            } else {
                setOptions([]);
            }
        },
        ...props.ajaxOptions
    };

    const searchOptions = {
        enabled: true,
        url: routes.Listings,
        text: translator.translate('sandhills.search'),
        additionalData: null,
        dataKey: 'keywords',
        useSubmitBehavior: false,
        onSearch: null,
        isQuickFindCode: (v) => quickFindRegex.test(v),
        runQuickFindCheck: () => {
            var qfOption = options.find(o => o.TermType == 'QuickFind');
            if (qfOption) {
                window.location.href = qfOption.Url;
            } else {
                sandhillsRequest({
                    url: routes.GetQuickFindUrl,
                    data: {
                        quickFindCode: inputValue
                    },
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((data) => {
                    if (data) {
                        window.location.href = data;
                    } else {
                        searchOptions.defaultSearch();
                    }
                }).catch(() => {
                    searchOptions.defaultSearch();
                });
            }
        },
        defaultSearch: () => {
            var url = '';
            if (searchType == 'Part') {
                url = buildUrl(props.partsUrl, 'PartNumber', inputValue);
                if (inputValue) {
                    searchOptions.additionalData = { SearchType: props.matchType ?? 'Start' };
                }
            }
            else if (searchType == 'Attachment') {
                url = buildUrl(props.attachmentsUrl, searchOptions.dataKey, inputValue);
            }
            else if (searchType == 'Dismantled') {
                url = buildUrl(routes.Dismantled, searchOptions.dataKey, inputValue);
            }
            else {
                url = buildUrl(searchOptions.url, searchOptions.dataKey, inputValue);
            }
            if (searchOptions.additionalData) {
                for (var key in searchOptions.additionalData) {
                    url = buildUrl(url, key, searchOptions.additionalData[key]);
                }
            }
            if (props.showHistory) {
                setSearchHistoryCookie(inputValue, url);
            }

            window.location.href = url;
        },
        runSearch: () => {
            setOpen(false);
            if (searchOptions.useSubmitBehavior) {
                return; // Means this is wrapped in a Form Tag
            } else if (searchOptions.url && !searchOptions.onSearch) {
                setOpenBackdrop(true);
                trackingOptions.save(inputValue);

                if (searchOptions.isQuickFindCode(inputValue)) {
                    searchOptions.runQuickFindCheck();
                } else {
                    searchOptions.defaultSearch();
                }

            } else {
                if (searchOptions.onSearch && typeof searchOptions.onSearch === 'function') {
                    searchOptions.onSearch(inputValue);
                }
            }
            setTimeout(() => {
                setOpenBackdrop(false);
                setIsLoading(false);
                if (props.setBackdropVisible) {
                    props.setBackdropVisible(false);
                    props.setIsFocused(false);
                }
            }, 1500);
        },
        ...props.searchOptions
    };

    const endAdorn = (endAdornment) => {
        const newChildren = React.Children.map(endAdornment?.props?.children || [], c => c);

        if (isLoading && !props.autocompleteSearchField) {
            newChildren?.unshift(<CircularProgress color='inherit' size={20} />);
        }

        if (searchOptions.enabled) {

            if (props.enableVoiceToText) {
                if (props.isListening) {
                    newChildren?.push(
                        <button className="voice-btn" onClick={props.onListenStop} aria-label="Stop Voice To Text">
                            <i className="fa-solid fa-square"></i>
                        </button>
                    );
                }
                else {
                    newChildren?.push(
                        <button className="voice-btn" onClick={props.onListenStart} aria-label="Start Voice To Text">
                            <i className="fa-solid fa-microphone"></i>
                        </button>
                    );
                }
            }

            newChildren?.push(
                <SearchIconButton key={searchOptions.text} type='submit' onClick={(e) => { e.preventDefault(); e.stopPropagation(); searchOptions.runSearch(); }} aria-label={searchOptions.text} size={size}>
                    <SearchIcon fontSize='small' />
                </SearchIconButton>
            );
        }

        const c = React.cloneElement(endAdornment ? endAdornment :
            <div
                style={{
                    position: 'absolute',
                    right: '9px',
                    top: 'calc(50% - 16px)'
                }}
            />,
            endAdornment?.props,
            newChildren);
        return c;
    }

    const trackingOptions = {
        url: routes.SaveAction,
        enabled: true,
        save: (value) => {
            if (trackingOptions.enabled) {
                trackAutocompleteSearchAction(trackingOptions.url, value);
            }
        },
        ...props.trackingOptions
    };

    const styles = {
        bgVariant: null,
        fontWeight: {
            normal: 400,
            highlight: 700
        },
        listbox: {
            maxHeight: 500,
            border: '1px solid #ccc'
        },
        ...props.styles
    };

    const inputOptions = {
        startAdornment: (value) => props.isListening ? (
            <ListeningIndicator className="listening-indicator">
                <span className="listening-text">Listening</span>
                <div className="soundwave">
                    <div className="wave"></div>
                    <div className="wave"></div>
                    <div className="wave"></div>
                    <div className="wave"></div>
                    <div className="wave"></div>
                </div>
            </ListeningIndicator>
        ) : null,
        ...props.inputOptions
    };

    /* MUI - Popper Props */
    const popperOptions = {
        placement: 'bottom-start',
        disablePortal: false,
        styles: null,

        ...props.popperOptions
    }

    const {
        width,
        textFieldStyles,
        isOptionEqualToValue,
        useAutoSuggestHighlight = true,
        disableClearable,
        disabled,
        /* MUI - Autocomplete Props */
        autoSelect = false,
        clearOnEscape,
        filterOptions = (x) => x,
        freeSolo = true,
        fullWidth = (width != null ? false : true),
        getOptionLabel = (option) => option.value || option,
        //groupBy = (option) => option.TermType,
        id: idProp,
        loadingText = translator.translate('sandhills.loading'),
        onChange = (event, newValue) => {
            if (newValue?.Url) {
                setOpenBackdrop(true);
                trackingOptions.save(newValue);
                if (props.showHistory) {
                    setSearchHistoryCookie(newValue.Label, newValue.Url);
                }
                setOpenBackdrop(false);
                window.location.href = newValue.Url;
            } else if (event?.key == 'Enter') {
                return searchOptions.runSearch();
            } else {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
            }
        },
        options: optionsProp,
        renderOption = (optionProps, option, { inputValue }) => {
            if (useAutoSuggestHighlight) {
                const matches = match(option.Label, inputValue);
                const parts = parse(option.Label, matches);

                return (
                    <Collapse className='autocomplete-popper' in={optionsVisible} appear={true} timeout={500}>
                        {props.showHistory && history && inputValue === '' && optionProps['data-option-index'] == 0 &&
                            <li>
                                <ListSubheader component="span" sx={{
                                    display: 'block',
                                    padding: '0',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    fontStyle: 'normal',
                                    lineHeight: '10px',
                                    color: '#333'
                                }}>
                                    {translator.translate('sandhills.tradesites.mvc.recentsearches')}
                                </ListSubheader>
                            </li>
                        }
                        {(history || inputValue.length > 0) &&
                            <li {...optionProps} style={{ width: '100%', display: 'block' }}>
                                <div style={{ paddingLeft: option?.Indent > 0 ? `${option.Indent * 10}px` : '' }}>
                                    {parts.map((part, index) => (
                                        <span key={index} style={{ fontWeight: part.highlight ? styles.fontWeight.highlight : styles.fontWeight.normal }}>
                                            {props.showHistory && inputValue === '' && <i className="fa-regular fa-clock media-buttons__icon"></i>}
                                            {part.text}
                                            {(index == parts.length - 1) && <i style={{ float: 'right' }} className="fa-solid fa-up-right-from-square"></i>}
                                        </span>
                                    ))}
                                </div>
                            </li>
                        }
                    </Collapse>
                );
            } else if (option?.isParent) {
                return (
                    <li {...optionProps} {...(option.key ? { key: option.key } : {})}>
                        <b>{getOptionLabel(option)}</b>
                    </li>
                );
            } else {
                return (
                    <li {...optionProps} {...(option.key ? { key: option.key } : {})}>
                        {getOptionLabel(option)}
                    </li>
                );
            }
        },
        size = 'small',
        value: valueProp,

        /* MUI - Textbox Props */
        onFocus,
        label = '',
        name: nameProp,
        helperText,
        onBlur,
        disableBlurDelay = false,
        variant,

        onOpen
    } = props;

    const [isOpen, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState(optionsProp || []);
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [searchType, setSearchType] = useState(props.searchType);
    const [popperOpacity, setPopperOpacity] = useState(1);

    const buildUrl = useCallback((urlToBuild, dataKey, searchValue) => {
        let url = urlToBuild;

        if (dataKey === 'PartNumber' && !searchValue) {
            url = routes.Parts_Landing;
        }

        if (!url.endsWith('=') && searchValue) {
            url += url.includes('?') ? '&' : '?';
            url += `${dataKey}=`;
        }
        url += `${encodeURIComponent(searchValue)}`;

        if (props.isStickyHeader && !url.includes('sh=')) {
            url += `${url.includes('?') ? '&' : '?'}sh=1`;
        }

        return url;
    }, []);

    const ajaxFetch = useMemo(
        () => throttle((active, inputValue, searchType) => ajaxOptions.onSearch(active, inputValue, searchType), 200),
        [props.ajaxOptions, props.isFocused]
    );

    useEffect(() => {
        setSearchType(props.searchType);
    }, [props.searchType]);

    useEffect(() => {
        setOptions(optionsProp || []);
    }, [optionsProp]);

    useEffect(() => {
        setHistory(getSearchHistoryValues());
    }, []);

    useEffect(() => {
        let active = true;

        if (inputValue.length < 1) {
            if (props.showHistory && history) {
                setOptions(history);
            }
            return undefined;
        }

        else if (inputValue.length < ajaxOptions.minLength && ajaxOptions.enabled) {
            setOptions([]);
            return undefined;
        }

        if (ajaxOptions.url && ajaxOptions.enabled) {
            ajaxFetch(active, inputValue, searchType);
        }

        return () => {
            active = false;
        };
    }, [value, inputValue, isOpen, searchType, props.isFocused]);

    // Reset options visibility when open state changes
    useEffect(() => {
        if (isOpen) {
            setOptionsVisible(true);
            setPopperOpacity(1);
        }
    }, [isOpen]);

    const getListBoxProps = () => {
        var lbProps = {
            style: {
                maxHeight: styles.listbox.maxHeight,
                border: styles.listbox.border
            }
        };

        return lbProps;
    };

    const onInputChange = (event, newInputValue) => {
        if (event?.key != 'Enter') {
            setInputValue(newInputValue);
        }
    };

    const onKeyDown = (e) => {
        if (e?.key === 'Enter' && props.autocompleteSearchField) {
            searchOptions.runSearch();
        }
    };

    const handleBlur = (event) => {
        // Clear any existing timeout
        if (collapseTimeoutRef.current) {
            clearTimeout(collapseTimeoutRef.current);
        }

        if (disableBlurDelay) {
            setOpen(false);
            if (onBlur) {
                onBlur(event);
            }
            return;
        }

        // Start collapsing animation for both options and popper
        setOptionsVisible(false);
        setPopperOpacity(0);

        // After animation completes, close the popper
        collapseTimeoutRef.current = setTimeout(() => {
            setOpen(false);
            // Call the original onBlur if provided
            if (onBlur) {
                onBlur(event);
            }
        }, 500); // Match collapse animation duration
    };

    const CustomPopper = (popperProps) => {
        // Store the reference to the popper element
        const { style, ...other } = popperProps;

        return (
            <Popper
                {...other}
                ref={popperRef}
                disablePortal={popperOptions.disablePortal}
                placement={popperOptions.placement}
                style={{
                    ...style,
                    opacity: popperOpacity,
                    transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 999999,
                    ...(popperOptions.styles || {})
                }}
                transition
            />
        );
    };
    const handleInputOnClick = () => {
        if (props.setBackdropVisible) {
            props.setIsFocused(true);
            props.setBackdropVisible(true);
            setOpen(true);
        }
    }
    const renderInput = (params) => {
        const selectedValue = params.inputProps.value;
        return (
            <TextField
                {...params}
                className='autocomplete-textfield'
                name={nameProp}
                label={(props.enableVoiceToText && props.isListening) ? '' : label}
                variant={variant}
                bgVariant={styles.bgVariant}
                onBlur={handleBlur}
                onClick={handleInputOnClick}
                onFocus={onFocusHandler}
                helperText={helperText}
                error={helperText ? true : false}
                hiddenLabel={props.hideLabel ? true : false}
                inputRef={ref}
                disabled={props.isListening}
                InputProps={{
                    ...params.InputProps,
                    startAdornment: inputOptions.startAdornment ? inputOptions.startAdornment(selectedValue) : null,
                    endAdornment: useMemo(() => endAdorn(params.InputProps.endAdornment), [isLoading, searchOptions.enabled, params.InputProps.endAdornment, props.isListening, props.enableVoiceToText, searchType, inputValue]),
                    style: {
                        ...textFieldStyles,
                        caretColor: props.isListening ? 'transparent' : 'inherit' // Hide cursor when listening
                    },
                    disableUnderline: props.disableUnderline ? true : false,
                    readOnly: props.isListening // Make input read-only when listening
                }}
            />
        );
    };

    const onOpenHandler = () => {
        // Clear any existing collapse timeout
        if (collapseTimeoutRef.current) {
            clearTimeout(collapseTimeoutRef.current);
            collapseTimeoutRef.current = null;
        }

        // Ensure options are visible and popper is opaque for animation
        setOptionsVisible(true);
        setPopperOpacity(1);

        if (onOpen && (!options || options.length <= 0 || (options.length == 1 && options[0].isDefault || options.length == 2 && options[1].isDefault))) {
            setIsLoading(true);
            onOpen?.()?.then(() => {
                setOpen(true);
                setIsLoading(false);
            })?.catch(() => {
                setOpen(true);
                setIsLoading(false);
            });
        }
        else {
            setOpen(true);
        }
    };

    const onFocusHandler = (event) => {
        if (collapseTimeoutRef.current) {
            clearTimeout(collapseTimeoutRef.current);
            collapseTimeoutRef.current = null;
        }
        try { onFocus && onFocus(event); } catch (err) { }
    }

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (collapseTimeoutRef.current) {
                clearTimeout(collapseTimeoutRef.current);
            }
        };
    }, []);

    return (
        <>
            <MuiAutocomplete
                className='autocomplete'
                autoSelect={autoSelect}
                clearOnEscape={clearOnEscape}
                filterOptions={freeSolo ? filterOptions : onFilterOptions}
                freeSolo={freeSolo}
                fullWidth={fullWidth}
                getOptionLabel={getOptionLabel}
                //groupBy={groupBy}
                id={idProp}
                ListboxProps={getListBoxProps()}
                loading={isLoading}
                loadingText={loadingText}
                onChange={onChange}
                onClose={(e, r) => {
                    setOpen(false);
                }}
                onInputChange={onInputChange}
                onOpen={onOpenHandler}
                open={!props.isListening && (isOpen || props.isFocused)}
                options={options}
                openOnFocus={props.openOnFocus}
                renderInput={renderInput}
                renderOption={renderOption}
                size={size}
                value={props.isListening ? '' : valueProp}  // Clear value during mic request too
                sx={{ width: width }}
                disableClearable={disableClearable}
                disabled={disabled}
                isOptionEqualToValue={isOptionEqualToValue}
                onKeyDown={onKeyDown}
                slots={{ popper: CustomPopper }}
                slotProps={{
                    paper: {
                        elevation: 0
                    }
                }}
            />
            <Backdrop
                open={openBackdrop}
                onClick={() => setOpenBackdrop(false)}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress />
            </Backdrop>
        </>
    );
});

export default Autocomplete;